import { createCriteriaParameters, standardDeleteQuery, standardGetQuery, standardPostQuery, standardPutQuery } from "./apiUtils.js";

import { replacePlaceholdersWithObjValues, setDeleteArrayState, setInsertArrayState } from "../shared/miscUtils";

import { SNACKBAR_SUCCESS_CREATED, SNACKBAR_SUCCESS_DELETED, SNACKBAR_SUCCESS_UPDATED } from "../../config/language/english";
import * as Constants from "../../config/constants/Constants.js";

/**
 * Function for querying transcripts with filters, pagination, sorting etc.
 */
export const queryTranscripts = (props, options = {}) => {
    const path = Constants.API_TRANSCRIPTS + createCriteriaParameters(options);

    standardGetQuery(props, path, (response) => {
        const states = props.backend.transcripts;

        setInsertArrayState(states.setData, response.body.elements);
        states.setQueryLoading(false);

        if (options.pagination === false) return;

        if (options.page !== undefined) states.setQueriedUntil(options.page);
        if (response?.body?.total !== undefined) states.setRowCount(response.body.total);
    });
};

/**
 * Function for querying an existing transcript.
 */
export const queryTranscript = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_TRANSCRIPTS_ID, { transcript_id: options.id });

    standardGetQuery(props, path, (response) => {
        const states = props.backend.transcripts;

        setInsertArrayState(states.setData, [{ ...response.body }]);
    });
};

/**
 * Function for creating a new transcript.
 */
export const createTranscript = (props, options = {}) => {
    const path = Constants.API_TRANSCRIPTS;

    standardPostQuery(props, path, options.body, (response) => {
        const states = props.backend.transcripts;

        setInsertArrayState(states.setData, [{ ...response.body }]);
        states.setRowCount((prev) => prev + 1);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_CREATED });
    });
};

/**
 * Function for updating a transcript.
 */
export const updateTranscript = (props, options = {}, callback = null) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_TRANSCRIPTS_ID, { transcript_id: options.id });

    standardPutQuery(props, path, options.body, (response) => {
        const states = props.backend.transcripts;

        setInsertArrayState(states.setData, [{ ...response.body }]);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
        if (callback) callback(response);
    });
};

/**
 * Function for approving a transcript.
 */
export const approveTranscript = (props, options = {}, callback = null) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_TRANSCRIPTS_ID_APPROVE, { transcript_id: options.id });

    standardPutQuery(props, path, undefined, (response) => {
        const states = props.backend.transcripts;

        setInsertArrayState(states.setData, [{ ...response.body }]);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
        if (callback) callback(response);
    });
};

/**
 * Function for approving a transcript.
 */
export const approveTranscripts = (props, options = {}) => {
    standardPutQuery(props, Constants.API_TRANSCRIPTS_APPROVE, options.body, (response) => {
        const states = props.backend.transcripts;

        setInsertArrayState(states.setData, response.body);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for retrying a transcript.
 */
export const retryTranscript = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_TRANSCRIPTS_ID_RETRY, { transcript_id: options.id });

    standardPutQuery(props, path, undefined, (response) => {
        const states = props.backend.transcripts;

        setInsertArrayState(states.setData, [{ ...response.body }]);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for retrying a transcript.
 */
export const retryTranscripts = (props, options = {}) => {
    standardPutQuery(props, Constants.API_TRANSCRIPTS_RETRY, options.body, (response) => {
        const states = props.backend.transcripts;

        setInsertArrayState(states.setData, response.body);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for retrying LLM caption cleanup from original ingest lines.
 */
export const retryCleanupTranscript = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_TRANSCRIPTS_ID_RETRY_CLEANUP, { transcript_id: options.id });

    standardPutQuery(props, path, undefined, (response) => {
        const states = props.backend.transcripts;

        setInsertArrayState(states.setData, [{ ...response.body }]);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

/**
 * Function for retrying LLM caption cleanup for multiple transcripts.
 */
export const retryCleanupTranscripts = (props, options = {}) => {
    standardPutQuery(props, Constants.API_TRANSCRIPTS_RETRY_CLEANUP, options.body, (response) => {
        const states = props.backend.transcripts;

        setInsertArrayState(states.setData, response.body);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
    });
};

// -------
// Transcript quotes
// -------

/**
 * Function for querying quotes with filters, pagination, sorting etc.
 */
export const queryQuotes = (props, options = {}) => {
    const path = Constants.API_QUOTES + createCriteriaParameters(options);

    standardGetQuery(props, path, (response) => {
        const states = props.backend.quotes;

        setInsertArrayState(states.setData, response.body.elements);
        states.setQueryLoading(false);

        if (options.page !== undefined) states.setQueriedUntil(options.page);
        if (response?.body?.total !== undefined) states.setRowCount(response.body.total);
    });
};

/**
 * Function for querying an existing quote.
 */
export const queryTranscriptQuotes = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_TRANSCRIPT_ID_QUOTES, { transcript_id: options.id });

    standardGetQuery(props, path, (response) => {
        const states = props.backend.quotes;

        setInsertArrayState(states.setData, response.body);
    });
};

/**
 * Function for creating a new quote.
 */
export const createQuote = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_TRANSCRIPT_ID_QUOTES, { transcript_id: options.id });

    standardPostQuery(props, path, options.body, (response) => {
        const states = props.backend.quotes;

        setInsertArrayState(states.setData, [{ ...response.body }]);
        states.setRowCount((prev) => prev + 1);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_CREATED });
    });
};

/**
 * Function for updating a quote.
 */
export const updateQuote = (props, options = {}, callback = null) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_QUOTES_ID, { quote_id: options.id });

    standardPutQuery(props, path, options.body, (response) => {
        const states = props.backend.quotes;

        setInsertArrayState(states.setData, [{ ...response.body }]);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
        if (callback) callback(response);
    });
};

/**
 * Function for deleting a quote.
 */
export const deleteQuote = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_QUOTES_ID, { quote_id: options.id });

    standardDeleteQuery(props, path, undefined, (response) => {
        const states = props.backend.quotes;

        setDeleteArrayState(states.setData, [options.id]);

        states.setRowCount(states.rowCount - 1);
        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_DELETED });
    });
};

/**
 * Function for deleting quote(s).
 */
export const deleteQuotes = (props, options = {}) => {
    const path = Constants.API_QUOTES;

    standardDeleteQuery(props, path, options.body, (response) => {
        const states = props.backend.quotes;

        setDeleteArrayState(states.setData, options.ids);

        states.setRowCount(states.rowCount - options.ids.length);
        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_DELETED });
    });
};
